/**
 * Metadata operacional del catálogo — SOLO admin/backoffice.
 * No afecta runtime, gating ni marketing hasta que se cablee explícitamente.
 */
import { SM_FEATURES, type FeatureLifecycle, type SmFeatureDef } from "@/lib/feature-catalog";

export type OperationalDomain =
  | "sales"
  | "operations"
  | "customers"
  | "scale"
  | "integrations"
  | "experimental"
  | "legacy";

export type FeatureType =
  | "core"
  | "addon"
  | "integration"
  | "experimental"
  | "enterprise"
  | "deprecated";

export type MaturityLevel = "ga" | "beta" | "preview" | "sunset";

export type FeatureFilterId =
  | "core"
  | "byo"
  | "integrations"
  | "beta"
  | "experimental"
  | "hidden_marketing"
  | "enterprise"
  | "addons";

export type FeatureOrigin = "core" | "byo";

export type ProviderCategory = "payments" | "logistics" | "messaging" | "invoicing" | "integrations";

export interface FeatureAdminMeta {
  operationalDomain: OperationalDomain;
  featureType: FeatureType;
  featureOrigin: FeatureOrigin;
  providerCategory?: ProviderCategory;
  maturityLevel: MaturityLevel;
  upgradeDriver?: boolean;
  recommendedPlanFloor: SmFeatureDef["planTier"];
  requiresSetup?: boolean;
  requiresProvider?: boolean;
  hiddenFromMarketing?: boolean;
  requires?: string[];
  aliases?: string[];
  tags?: string[];
}

/** Secciones comerciales admin — Core vs BYO. */
export const FM_COMMERCIAL_ORIGIN_SECTIONS = [
  {
    id: "core" as const,
    name: "Core Structura",
    description: "Plataforma incluida en el plan — no requiere cuenta externa",
  },
  {
    id: "byo" as const,
    name: "Compatible con tus herramientas",
    description: "Conectás tu proveedor (BYO) — disponible según tier",
  },
] as const;

export interface EnrichedFeature extends SmFeatureDef {
  admin: FeatureAdminMeta;
}

export const FM_OPERATIONAL_DOMAINS: {
  id: OperationalDomain;
  name: string;
  description: string;
  defaultOpen: boolean;
}[] = [
  { id: "sales", name: "Ventas & storefront", description: "Tienda, pedidos, checkout, catálogo, QR", defaultOpen: true },
  { id: "operations", name: "Operación diaria", description: "Stock, peso, impresión, logística, POS", defaultOpen: true },
  { id: "customers", name: "Clientes & fidelización", description: "Cuentas, puntos, gift cards, WhatsApp", defaultOpen: false },
  { id: "scale", name: "Escala & marca", description: "Branding, dominio, analytics, B2B, equipo", defaultOpen: false },
  { id: "integrations", name: "Integraciones (providers)", description: "Pasarelas y couriers — no son features core", defaultOpen: true },
  { id: "experimental", name: "Experimental & upcoming", description: "En evolución, no vendible aún", defaultOpen: false },
  { id: "legacy", name: "Legacy / deprecado", description: "En retiro — oculto por defecto en matriz", defaultOpen: false },
];

/** Packs preparados — aún no habilitados en runtime. */
export const FM_PACK_CATALOG_PLACEHOLDER = [
  { id: "pack_team_member", name: "Pack usuarios / asientos", unit: "miembros del equipo", status: "available" as const },
  { id: "pack_emails", name: "Pack emails", unit: "emails/día", status: "planned" as const },
  { id: "pack_push", name: "Pack push", unit: "notificaciones/día", status: "planned" as const },
  { id: "pack_wa", name: "Pack WhatsApp", unit: "mensajes/mes", status: "planned" as const },
  { id: "pack_storage", name: "Pack almacenamiento", unit: "imágenes / GB", status: "planned" as const },
] as const;

/**
 * Providers BYO explícitos (cuenta externa del merchant).
 * inferFeatureOrigin también auto-detecta payment_*, *_shipping y delivery_provider_* nuevos.
 */
const BYO_FEATURE_IDS = new Set([
  // Pagos (providers)
  "payment_mercadopago",
  "payment_payway",
  "payment_modo",
  "payment_getnet",
  "payment_uala",
  // Logística / couriers
  "shipping_shipnow",
  "shipping_tracking",
  "shipping_multi_carrier",
  "treggo_shipping",
  "andreani_shipping",
  "enviopack_shipping",
  "cabify_shipping",
  "uber_direct_shipping",
  "zipnova_shipping",
  "pickit_shipping",
  "oca_epak_shipping",
  "correo_argentino_shipping",
  "delivery_provider_pedidosya",
  "delivery_provider_rapiboy",
  "delivery_provider_shipnow",
  // Hub delivery (capa provider)
  "delivery_tracking",
  "delivery_quotes",
  "delivery_webhooks",
  "delivery_dispatch",
  "delivery_pickup",
  "delivery_eta",
  // Messaging / fiscal
  "whatsapp_notify",
  "invoicing_tusfacturas",
  "invoicing_afip_direct",
]);

/** Capabilities de pago de plataforma (NO son BYO provider). */
const PLATFORM_PAYMENT_IDS = new Set([
  "payment_online",
  "payment_qr",
  "payment_links",
  "payment_partial",
  "payment_refunds",
  "payment_recurring",
  "payment_marketplace_ready",
  "payment_pos_mode",
]);

function isByoProviderFeature(id: string): boolean {
  if (BYO_FEATURE_IDS.has(id)) return true;
  if (id.endsWith("_shipping") || id.startsWith("delivery_provider_")) return true;
  if (id.startsWith("payment_") && !PLATFORM_PAYMENT_IDS.has(id)) return true;
  if (id.startsWith("invoicing_")) return true;
  return false;
}

/** @deprecated alias — usar isByoProviderFeature */
const INTEGRATION_IDS = BYO_FEATURE_IDS;

function isLogisticsFeature(id: string): boolean {
  return (
    id.startsWith("shipping_") ||
    id.startsWith("delivery_provider_") ||
    id.endsWith("_shipping") ||
    id === "delivery_tracking" ||
    id === "delivery_quotes" ||
    id === "delivery_webhooks" ||
    id === "delivery_dispatch" ||
    id === "delivery_pickup" ||
    id === "delivery_eta" ||
    id === "delivery_zones"
  );
}

function inferProviderCategory(id: string): ProviderCategory | undefined {
  if (id.startsWith("payment_") || id === "payment_mercadopago") return "payments";
  if (isLogisticsFeature(id)) return "logistics";
  if (id === "whatsapp_notify") return "messaging";
  if (id === "push_notifications" || id === "email_automations") return "messaging";
  if (id === "invoicing_tusfacturas" || id.startsWith("invoicing_")) return "invoicing";
  if (id === "integrations_meta" || id === "integrations_ga4" || id === "integrations_clarity" || id === "custom_head_scripts") return "integrations";
  return undefined;
}

function inferFeatureOrigin(id: string): FeatureOrigin {
  return isByoProviderFeature(id) ? "byo" : "core";
}

const ADDON_IDS = new Set(["loyalty_points", "gift_cards"]);

const ENTERPRISE_IDS = new Set(["custom_domain", "payment_getnet", "shipping_multi_carrier"]);

function lifecycleToMaturity(lc: FeatureLifecycle): MaturityLevel {
  if (lc === "stable") return "ga";
  if (lc === "beta") return "beta";
  if (lc === "upcoming") return "preview";
  return "sunset";
}

function inferDomain(id: string, f: SmFeatureDef): OperationalDomain {
  if (f.lifecycle === "deprecated") return "legacy";
  // Providers BYO (pasarelas, couriers, WhatsApp, facturación) → dominio Integraciones
  if (isByoProviderFeature(id)) return "integrations";
  // Commerce Growth — customers (antes del fallback upcoming → experimental)
  if (
    id === "product_reviews" ||
    id === "referral_rewards" ||
    id === "social_proof" ||
    id === "loyalty_tiers"
  ) {
    return "customers";
  }
  // Commerce Growth — sales (wheel / abandoned / campaigns)
  if (
    id === "discount_wheel" ||
    id === "abandoned_cart_incentives" ||
    id === "growth_campaigns"
  ) {
    return "sales";
  }
  if (f.lifecycle === "upcoming" || id === "analytics_advanced") return "experimental";
  if (
    id === "catalog_public" ||
    id === "orders_online" ||
    id === "widget_embed" ||
    id === "payment_qr" ||
    id === "variants_addons" ||
    id === "feature_presets" ||
    id === "payment_links" ||
    id === "combos" ||
    id === "catalog_public" ||
    id === "orders_online" ||
    id === "payment_online"
  ) {
    return "sales";
  }
  // Logística no-provider (p.ej. delivery_zones interno) sigue en operations
  if (isLogisticsFeature(id) && !isByoProviderFeature(id)) return "operations";
  if (
    id === "pesaje_module" ||
    id === "stock_management" ||
    id === "multi_location" ||
    id === "shipping_tracking" ||
    id === "payment_pos_mode" ||
    id === "exports_csv" ||
    id === "payment_refunds" ||
    id === "thermal_printing" ||
    id === "delivery_zones" ||
    id === "stock_management" ||
    id === "inventory_advanced" ||
    id === "operations_suppliers" ||
    id === "payment_config" ||
    id === "product_tags" ||
    id === "bulk_prices" ||
    id === "price_lists" ||
    id === "media"
  ) {
    return "operations";
  }
  if (
    id === "customer_accounts" ||
    id === "loyalty_points" ||
    id === "gift_cards" ||
    id === "whatsapp_notify" ||
    id === "pwa_install" ||
    id === "product_tours" ||
    id === "tenant_support"
  ) {
    return "customers";
  }
  if (
    id === "custom_branding" ||
    id === "custom_domain" ||
    id === "produccion_b2b" ||
    id === "institutional_orders" ||
    id === "billing_lemon" ||
    id === "payment_marketplace_ready" ||
    id === "shipping_multi_carrier" ||
    id === "api_keys" ||
    id === "storefront_experience" ||
    id === "feature_presets" ||
    id === "sample_data" ||
    id === "institutional_orders"
  ) {
    return "scale";
  }
  if (id.startsWith("payment_")) return "sales";
  return "operations";
}

function inferType(id: string, f: SmFeatureDef): FeatureType {
  if (f.lifecycle === "deprecated") return "deprecated";
  if (INTEGRATION_IDS.has(id)) return "integration";
  if (ADDON_IDS.has(id)) return "addon";
  if (ENTERPRISE_IDS.has(id)) return "enterprise";
  if (f.lifecycle === "upcoming") return "experimental";
  return "core";
}

const REQUIRES: Record<string, string[]> = {
  shipping_tracking: ["shipping_shipnow"],
  delivery_tracking: [
    "delivery_provider_pedidosya",
    "delivery_provider_rapiboy",
    "delivery_provider_shipnow",
    "cabify_shipping",
    "uber_direct_shipping",
  ],
  delivery_quotes: [
    "delivery_provider_pedidosya",
    "delivery_provider_rapiboy",
    "cabify_shipping",
    "uber_direct_shipping",
  ],
  delivery_webhooks: [
    "delivery_provider_pedidosya",
    "delivery_provider_rapiboy",
    "uber_direct_shipping",
  ],
  payment_online: ["payment_mercadopago", "payment_payway", "payment_uala"],
  payment_partial: ["payment_online"],
  payment_recurring: ["payment_payway"],
  loyalty_points: ["customer_accounts"],
  gift_cards: ["customer_accounts"],
  institutional_orders: ["produccion_b2b"],
  loyalty_tiers: ["loyalty_points"],
  referral_rewards: ["customer_accounts"],
  discount_wheel: ["discount_coupons"],
  abandoned_cart_incentives: ["discount_coupons"],
};

const ALIASES: Record<string, string[]> = {
  catalog_public: ["storefront", "tienda", "market"],
  orders_online: ["checkout", "carrito", "pedidos"],
  widget_embed: ["iframe", "embebido"],
  payment_mercadopago: ["mp", "mercadopago"],
  payment_payway: ["decidir", "tarjeta"],
  payment_uala: ["uala bis", "ualá"],
  shipping_shipnow: ["envíos", "logística", "courier"],
  cabify_shipping: ["cabify", "same day", "courier"],
  uber_direct_shipping: ["uber", "uber direct", "uber deliveries"],
  delivery_provider_pedidosya: ["pedidosya", "peya", "pedidos ya"],
  delivery_provider_rapiboy: ["rapiboy", "rapi boy"],
  delivery_provider_shipnow: ["shipnow delivery", "ship now"],
  delivery_tracking: ["tracking delivery", "seguimiento courier"],
  delivery_quotes: ["cotización delivery", "quote shipping"],
  loyalty_points: ["puntos", "fidelización"],
  gift_cards: ["giftcard", "tarjeta regalo"],
  invoicing_tusfacturas: ["facturación", "afip", "arca", "factura", "cae", "tusfacturas"],
  invoicing_afip_direct: ["facturación", "afip", "arca", "factura", "cae", "caea", "wsaa", "wsfe", "certificado"],
};

/** Overrides explícitos — el resto se infiere. */
const ADMIN_OVERRIDES: Partial<Record<string, Partial<FeatureAdminMeta>>> = {
  payment_online: { requiresSetup: true },
  shipping_shipnow: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  shipping_tracking: { requiresSetup: true, requiresProvider: true },
  treggo_shipping: { requiresSetup: true, requiresProvider: true },
  andreani_shipping: { requiresSetup: true, requiresProvider: true },
  enviopack_shipping: { requiresSetup: true, requiresProvider: true },
  cabify_shipping: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  uber_direct_shipping: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  zipnova_shipping: { requiresSetup: true, requiresProvider: true },
  pickit_shipping: { requiresSetup: true, requiresProvider: true },
  delivery_provider_pedidosya: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  delivery_provider_rapiboy: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  delivery_provider_shipnow: { requiresSetup: true, requiresProvider: true },
  delivery_tracking: { requiresSetup: true },
  delivery_webhooks: { requiresSetup: true },
  payment_payway: { requiresProvider: true },
  payment_uala: { requiresProvider: true },
  payment_mercadopago: { requiresProvider: true },
  invoicing_tusfacturas: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  invoicing_afip_direct: { requiresSetup: true, requiresProvider: true, upgradeDriver: true },
  custom_domain: { upgradeDriver: true, requiresSetup: true },
  loyalty_points: { upgradeDriver: true },
  gift_cards: { upgradeDriver: true },
  variants_addons: { upgradeDriver: true },
  customer_accounts: { upgradeDriver: true },
  multi_location: { upgradeDriver: true },
  custom_branding: { upgradeDriver: true },
  analytics_advanced: { upgradeDriver: true },
  institutional_orders: { upgradeDriver: true },
  produccion_b2b: { upgradeDriver: true },
  whatsapp_notify: { upgradeDriver: true },
  pwa_install: { upgradeDriver: true },
};

/** Parcial persistido en DB — null en booleanos = borrar override explícito. */
export type FeatureAdminMetaDbPatch = Partial<
  Omit<FeatureAdminMeta, "requires" | "aliases" | "tags" | "providerCategory">
> & {
  providerCategory?: ProviderCategory | null;
  requires?: string[];
  aliases?: string[];
  tags?: string[];
  notes?: string | null;
};

function buildAdminMeta(f: SmFeatureDef, db?: FeatureAdminMetaDbPatch): FeatureAdminMeta {
  const operationalDomain = inferDomain(f.id, f);
  const featureType = inferType(f.id, f);
  const ov = ADMIN_OVERRIDES[f.id] ?? {};
  const featureOrigin = inferFeatureOrigin(f.id);
  const base: FeatureAdminMeta = {
    operationalDomain,
    featureType,
    featureOrigin,
    providerCategory: inferProviderCategory(f.id),
    maturityLevel: lifecycleToMaturity(f.lifecycle),
    recommendedPlanFloor: f.planTier,
    hiddenFromMarketing: ov.hiddenFromMarketing ?? !f.marketingVisible,
    requiresSetup: ov.requiresSetup,
    requiresProvider: ov.requiresProvider ?? featureOrigin === "byo",
    upgradeDriver: ov.upgradeDriver,
    requires: REQUIRES[f.id],
    aliases: ALIASES[f.id],
    tags: [
      operationalDomain,
      featureType,
      featureOrigin,
      f.lifecycle,
      f.planTier,
      ...(ALIASES[f.id] ?? []),
    ],
    ...ov,
  };
  if (!db) return base;
  return mergeAdminMetaPatch(base, db);
}

export function mergeAdminMetaPatch(
  base: FeatureAdminMeta,
  patch: FeatureAdminMetaDbPatch,
): FeatureAdminMeta {
  const merged: FeatureAdminMeta = { ...base };
  if (patch.operationalDomain) merged.operationalDomain = patch.operationalDomain;
  if (patch.featureOrigin) merged.featureOrigin = patch.featureOrigin;
  if (patch.providerCategory) merged.providerCategory = patch.providerCategory;
  if (patch.featureType) merged.featureType = patch.featureType;
  if (patch.maturityLevel) merged.maturityLevel = patch.maturityLevel;
  if (patch.recommendedPlanFloor) merged.recommendedPlanFloor = patch.recommendedPlanFloor;
  if (patch.upgradeDriver != null) merged.upgradeDriver = patch.upgradeDriver;
  if (patch.requiresSetup != null) merged.requiresSetup = patch.requiresSetup;
  if (patch.requiresProvider != null) merged.requiresProvider = patch.requiresProvider;
  if (patch.hiddenFromMarketing != null) merged.hiddenFromMarketing = patch.hiddenFromMarketing;
  if (patch.requires?.length) merged.requires = patch.requires;
  if (patch.aliases?.length) {
    merged.aliases = [...new Set([...(base.aliases ?? []), ...patch.aliases])];
  }
  if (patch.tags?.length) merged.tags = patch.tags;
  return merged;
}

export function getEnrichedFeatures(
  dbOverrides?: Record<string, FeatureAdminMetaDbPatch>,
): EnrichedFeature[] {
  return SM_FEATURES.map((f) => ({
    ...f,
    admin: buildAdminMeta(f, dbOverrides?.[f.id]),
  }));
}

export function getEnrichedFeature(
  id: string,
  dbOverrides?: Record<string, FeatureAdminMetaDbPatch>,
): EnrichedFeature | undefined {
  const f = SM_FEATURES.find((x) => x.id === id);
  return f ? { ...f, admin: buildAdminMeta(f, dbOverrides?.[id]) } : undefined;
}

export function searchEnrichedFeatures(
  query: string,
  filters: Set<FeatureFilterId>,
  includeLegacy = false,
  dbOverrides?: Record<string, FeatureAdminMetaDbPatch>,
): EnrichedFeature[] {
  const q = query.trim().toLowerCase();
  return getEnrichedFeatures(dbOverrides).filter((f) => {
    if (!includeLegacy && f.admin.operationalDomain === "legacy") return false;
    if (filters.size > 0) {
      const matches = [
        filters.has("core") && f.admin.featureOrigin === "core",
        filters.has("byo") && f.admin.featureOrigin === "byo",
        filters.has("integrations") && f.admin.featureType === "integration",
        filters.has("addons") && f.admin.featureType === "addon",
        filters.has("beta") && f.lifecycle === "beta",
        filters.has("experimental") &&
          (f.admin.featureType === "experimental" || f.lifecycle === "upcoming"),
        filters.has("hidden_marketing") && f.admin.hiddenFromMarketing,
        filters.has("enterprise") && f.admin.featureType === "enterprise",
      ];
      if (!matches.some(Boolean)) return false;
    }
    if (!q) return true;
    const hay = [
      f.id,
      f.name,
      f.description,
      ...(f.admin.aliases ?? []),
      ...(f.admin.tags ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export interface PlanOverviewStats {
  core: number;
  integrations: number;
  addons: number;
  enterprise: number;
  experimental: number;
  beta: number;
  limitsFilled: number;
  limitsTotal: number;
}

export function computePlanOverviewStats(
  featureIds: string[],
  limits: Record<string, number | boolean | null | undefined>,
  limitKeys: readonly string[],
  dbOverrides?: Record<string, FeatureAdminMetaDbPatch>,
): PlanOverviewStats {
  const enriched = featureIds
    .map((id) => getEnrichedFeature(id, dbOverrides))
    .filter(Boolean) as EnrichedFeature[];
  const limitsFilled = limitKeys.filter((k) => limits[k] != null && (limits[k] as unknown) !== "").length;
  return {
    core: enriched.filter((f) => f.admin.featureType === "core").length,
    integrations: enriched.filter((f) => f.admin.featureType === "integration").length,
    addons: enriched.filter((f) => f.admin.featureType === "addon").length,
    enterprise: enriched.filter((f) => f.admin.featureType === "enterprise").length,
    experimental: enriched.filter((f) => f.admin.featureType === "experimental" || f.lifecycle === "upcoming").length,
    beta: enriched.filter((f) => f.lifecycle === "beta").length,
    limitsFilled,
    limitsTotal: limitKeys.length,
  };
}

export const FM_ADMIN_SECTION_STORAGE_KEY = "fm_admin_plan_catalog_sections_v1";

export function defaultSectionCollapsed(): Record<string, boolean> {
  const out: Record<string, boolean> = { lim: false, packs: false, matrix: false };
  for (const d of FM_OPERATIONAL_DOMAINS) {
    out[d.id] = !d.defaultOpen;
  }
  for (const o of FM_COMMERCIAL_ORIGIN_SECTIONS) {
    out[`origin-${o.id}`] = false; // Core + BYO abiertos
  }
  return out;
}

export function loadSectionCollapsed(): Record<string, boolean> {
  if (typeof window === "undefined") return defaultSectionCollapsed();
  try {
    const raw = localStorage.getItem(FM_ADMIN_SECTION_STORAGE_KEY);
    if (raw) return { ...defaultSectionCollapsed(), ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return defaultSectionCollapsed();
}

export function saveSectionCollapsed(state: Record<string, boolean>): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(FM_ADMIN_SECTION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
